import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface HastTextNode {
  type: 'text';
  value: string;
}

interface HastElementNode {
  type: 'element' | 'root';
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

type HastNode = HastTextNode | HastElementNode;

function splitTextNode(value: string, needle: string): HastNode[] {
  const lowerValue = value.toLowerCase();
  const nodes: HastNode[] = [];
  let cursor = 0;
  let index = lowerValue.indexOf(needle, cursor);

  while (index !== -1) {
    if (index > cursor) {
      nodes.push({ type: 'text', value: value.slice(cursor, index) });
    }
    nodes.push({
      type: 'element',
      tagName: 'span',
      properties: { className: ['text-yellow-500'] },
      children: [{ type: 'text', value: value.slice(index, index + needle.length) }],
    });
    cursor = index + needle.length;
    index = lowerValue.indexOf(needle, cursor);
  }

  if (cursor < value.length) {
    nodes.push({ type: 'text', value: value.slice(cursor) });
  }
  return nodes;
}

/** Rehype plugin: highlights every occurrence of `query` inside the rendered answer's text nodes. */
function rehypeHighlightText(query: string) {
  const needle = query.trim().toLowerCase();
  return (tree: HastElementNode) => {
    if (!needle) {
      return;
    }
    function visit(node: HastElementNode) {
      if (!node.children) {
        return;
      }
      const nextChildren: HastNode[] = [];
      for (const child of node.children) {
        if (child.type === 'text' && child.value.toLowerCase().includes(needle)) {
          nextChildren.push(...splitTextNode(child.value, needle));
        } else {
          if ('children' in child) {
            visit(child);
          }
          nextChildren.push(child);
        }
      }
      node.children = nextChildren;
    }
    visit(tree);
  };
}

const components: Components = {
  a: ({ children, ...props }) => (
    <a target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
  code: ({ children, className, ...props }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <pre className="overflow-x-auto rounded bg-muted p-3 text-xs text-foreground">
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      );
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 text-xs" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  img: ({ alt, ...props }) => (
    <img
      alt={alt ?? ''}
      className="max-w-full rounded-md border border-border"
      loading="lazy"
      {...props}
    />
  ),
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** When set, highlights every occurrence of this text within the rendered output. */
  highlightQuery?: string;
}

export function MarkdownRenderer({ content, className, highlightQuery }: MarkdownRendererProps) {
  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className ?? ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={highlightQuery?.trim() ? [() => rehypeHighlightText(highlightQuery)] : []}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
